<template>
  <div class="prod-page">
    <router-link to="/production/projects" class="inline-flex items-center gap-1 text-sm font-semibold move-text-muted hover:text-[var(--move-accent)] w-fit">
      <span class="material-icons text-base">arrow_back</span>
      Projects
    </router-link>

    <div v-if="error" class="move-alert-error">{{ error }}</div>
    <div v-else-if="loading" class="flex justify-center py-16">
      <div class="move-spinner" aria-label="Loading project" />
    </div>

    <template v-else-if="project">
      <ProductionPageHeader
        kicker="Project"
        :title="project.name"
        :subtitle="project.description || ''"
      >
        <template #badge>
          <span :class="statusBadgeClass(project.status)">{{ projectStatusLabels[project.status] || project.status }}</span>
        </template>
        <template #actions>
          <button type="button" class="move-btn-ghost" @click="toggleEdit">
            <span class="material-icons text-lg">{{ showEdit ? 'close' : 'edit' }}</span>
            {{ showEdit ? 'Close' : 'Edit project' }}
          </button>
          <button type="button" class="move-btn-primary" @click="showLineForm = !showLineForm">
            <span class="material-icons text-lg">{{ showLineForm ? 'close' : 'add' }}</span>
            {{ showLineForm ? 'Close' : 'Add production' }}
          </button>
        </template>
      </ProductionPageHeader>

      <form v-if="showEdit" class="move-card prod-panel space-y-5 max-w-3xl" @submit.prevent="saveProject">
        <ProductionSectionHead title="Project details" subtitle="Update the campaign name, status, and description." />
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="move-field !mb-0">
            <label for="edit-project-name">Name</label>
            <input id="edit-project-name" v-model="form.name" required class="move-input" />
          </div>
          <div class="move-field !mb-0">
            <label for="edit-project-status">Status</label>
            <MoveSelect id="edit-project-status" v-model="form.status" block :options="projectStatusOptions" />
          </div>
        </div>
        <div class="move-field !mb-0">
          <label for="edit-project-description">Description</label>
          <textarea id="edit-project-description" v-model="form.description" rows="3" class="move-textarea" />
        </div>
        <div class="prod-form-actions">
          <button type="submit" class="move-btn-primary" :disabled="saving">
            {{ saving ? 'Saving…' : 'Save project' }}
          </button>
          <button type="button" class="move-btn-ghost" @click="closeEdit">Cancel</button>
        </div>
      </form>

      <ProductionSectionHead
        title="Productions"
        :subtitle="`${(project.productions || []).length} shoot ${(project.productions || []).length === 1 ? 'line' : 'lines'} on this project.`"
      />

      <form v-if="showLineForm" class="move-card prod-panel flex flex-col sm:flex-row gap-3" @submit.prevent="createLine">
        <input v-model="lineName" required placeholder="Episode 1, Launch film…" class="move-input flex-1" />
        <div class="prod-form-actions">
          <button type="submit" class="move-btn-primary">Create</button>
          <button type="button" class="move-btn-ghost" @click="showLineForm = false">Cancel</button>
        </div>
      </form>

      <div v-if="!(project.productions || []).length" class="move-card move-empty-state py-12">
        <span class="material-icons">theaters</span>
        <p class="font-semibold move-text">No productions on this project yet</p>
        <p class="text-sm move-text-faint mt-1">Add a shoot line to start planning.</p>
      </div>
      <div v-else class="space-y-3">
        <router-link
          v-for="item in project.productions"
          :key="item.id"
          :to="`/production/productions/${item.id}`"
          class="prod-list-card"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-semibold move-text">{{ item.name }}</span>
            <span :class="statusBadgeClass(item.status)">{{ productionStatusLabels[item.status] || item.status }}</span>
          </div>
          <ProductionProgress class="mt-3" compact :status="item.status" />
          <ProductionGaps :gaps="item.gaps" />
        </router-link>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import ProductionPageHeader from '../../components/production/ProductionPageHeader.vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import ProductionProgress from '../../components/production/ProductionProgress.vue'
import ProductionGaps from '../../components/production/ProductionGaps.vue'
import { productionApi } from '../../services/productionApi'
import { productionStatusLabels, projectStatusLabels, projectStatusOptions, statusBadgeClass } from '../../config/productionNavigation'

const route = useRoute()
const loading = ref(true)
const error = ref('')
const saving = ref(false)
const project = ref(null)
const showLineForm = ref(false)
const showEdit = ref(false)
const lineName = ref('')
const form = reactive({ name: '', description: '', status: 'active' })

function syncForm (value) {
  form.name = value.name
  form.description = value.description || ''
  form.status = value.status
}

function closeEdit () {
  showEdit.value = false
  if (project.value) syncForm(project.value)
}

function toggleEdit () {
  if (showEdit.value) {
    closeEdit()
    return
  }
  if (project.value) syncForm(project.value)
  showEdit.value = true
}

async function load () {
  loading.value = true
  error.value = ''
  try {
    const body = await productionApi.showProject(route.params.projectId)
    project.value = body.data
    syncForm(body.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to load project.'
  } finally {
    loading.value = false
  }
}

async function saveProject () {
  saving.value = true
  try {
    const body = await productionApi.updateProject(route.params.projectId, { ...form })
    project.value = { ...project.value, ...body.data, productions: project.value.productions }
    showEdit.value = false
  } catch (e) {
    error.value = e.response?.data?.message || 'Could not save project.'
  } finally {
    saving.value = false
  }
}

async function createLine () {
  await productionApi.createProduction({ project_id: Number(route.params.projectId), name: lineName.value })
  lineName.value = ''
  showLineForm.value = false
  await load()
}

onMounted(load)
</script>
